import React from 'react';
import { Link } from 'react-router-dom';
import cn from '../utils/cn';
import KoobAvatar from './KoobAvatar';

type KoobEntityChipProps = {
  /** Shown beside the image; renders nothing when missing. */
  name?: string | null;
  /** Avatar, logo or photo; initials stand in when missing or broken. */
  image?: string | null;
  /** Links the chip to the record's page. */
  to?: React.ComponentProps<typeof Link>['to'];
  /** People are round, places and things square. */
  rounded?: 'full' | 'md';
  className?: string;
};

/**
 * Mini chip for a user, hotel, product or organization in a table cell:
 * a small image (or its initials) and the name, a link when `to` is given.
 */
export default function KoobEntityChip({
  name,
  image,
  to,
  rounded = 'full',
  className,
}: KoobEntityChipProps) {
  const label = name?.trim();
  if (!label) {
    return null;
  }

  const content = (
    <>
      <KoobAvatar src={image} name={label} size={6} rounded={rounded} />
      <span className="truncate">{label}</span>
    </>
  );
  const classes = cn(
    'inline-flex max-w-full items-center gap-2 align-middle',
    className,
  );

  return to ? (
    <Link
      to={to}
      // rows behind the chip can be clickable
      onClick={(event) => event.stopPropagation()}
      className={cn(classes, 'text-inherit no-underline hover:text-k-h')}
    >
      {content}
    </Link>
  ) : (
    <span className={classes}>{content}</span>
  );
}
